import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Tag,
  Spin,
  Typography,
  Table,
  Tooltip,
  Switch,
  Button,
  Select,
  Image,
  Row,
  Col,
  Space,
} from 'antd';
import {
  HistoryOutlined,
  EnvironmentOutlined,
  FilePdfOutlined,
  PictureOutlined,
  LoginOutlined,
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import SITELINKS from 'constants/sitelinks';
import type { TableColumnsType } from 'antd';
import {
  MapContainer,
  TileLayer,
  Marker,
  Circle,
  CircleMarker,
  Popup,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIconPng from 'leaflet/dist/images/marker-icon.png';
import markerShadowPng from 'leaflet/dist/images/marker-shadow.png';
import {
  useGetCollectionDetail,
  useGetCollectionShipments,
  CollectionShipment,
  CollectionDetailStore,
  CollectionGeofenceEvent,
  CollectionPickupLog,
  CollectionStatusLog,
  CollectionStatus,
} from 'services/CollectionsService';
import { exportCollectionRemito } from './collectionRemito';

const GEOFENCE_RADIUS_METERS = 200;

const { Text } = Typography;

const defaultIcon = L.icon({
  iconUrl: markerIconPng,
  shadowUrl: markerShadowPng,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const STATUS_LABELS: Record<CollectionStatus, string> = {
  pending_collection: 'Pendiente',
  in_transit_to_store: 'En tránsito',
  collected: 'Colectada',
  cancelled: 'Cancelada',
  received: 'Completada',
};

const STATUS_COLORS: Record<CollectionStatus, string> = {
  pending_collection: 'blue',
  in_transit_to_store: 'cyan',
  collected: 'orange',
  cancelled: 'red',
  received: 'green',
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  arrived: 'Llegada',
  enter: 'Llegada',
  departed: 'Salida',
  exit: 'Salida',
  leave: 'Salida',
};

const EVENT_TYPE_COLORS: Record<string, string> = {
  arrived: 'green',
  enter: 'green',
  departed: 'volcano',
  exit: 'volcano',
  leave: 'volcano',
};

const STORE_STATUS_LABELS: Record<string, string> = {
  pending_collection: 'Pendiente',
  in_transit_to_store: 'En transito',
  collected: 'Colectado',
  ingesting: 'Ingresando',
  received: 'Recibida en deposito',
  cancelled: 'Cancelada',
};

const STORE_STATUS_COLORS: Record<string, string> = {
  pending_collection: 'blue',
  in_transit_to_store: 'cyan',
  collected: 'orange',
  ingesting: 'purple',
  received: 'success',
  cancelled: 'red',
};

interface MapPoint {
  lat: number;
  lng: number;
  label: string;
  type: 'store' | 'deposit';
}

interface CollectionDetailModalProps {
  collectionId: number | null;
  open: boolean;
  onClose: () => void;
}

// ── helpers ──────────────────────────────────────────────────

const MapInvalidator: React.FC = () => {
  const map = useMap();
  useEffect(() => {
    // Ant Design modal open animation is ~300ms; invalidate after it completes
    const t = setTimeout(() => map.invalidateSize({ animate: false }), 350);
    return () => clearTimeout(t);
  }, [map]);
  return null;
};

const FlyToController: React.FC<{ point: [number, number] | null }> = ({
  point,
}) => {
  const map = useMap();
  useEffect(() => {
    if (!point) return;
    const [lat, lng] = point;
    if (!isFinite(lat) || !isFinite(lng)) return;
    map.invalidateSize({ animate: false });
    map.flyTo([lat, lng], 17, { duration: 0.8 });
  }, [point, map]);
  return null;
};

const InfoItem: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <Text
      type="secondary"
      style={{
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        display: 'block',
        marginBottom: 2,
      }}
    >
      {label}
    </Text>
    <div style={{ fontSize: 13 }}>{children}</div>
  </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Text
    strong
    style={{
      display: 'block',
      fontSize: 12,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: '#8c8c8c',
      marginBottom: 10,
    }}
  >
    {children}
  </Text>
);

const GeoBtn: React.FC<{
  lat: number | null | undefined;
  lng: number | null | undefined;
  onFly: (pt: [number, number]) => void;
}> = ({ lat, lng, onFly }) => {
  if (!lat || !lng) return null;
  return (
    <Tooltip title="Ver en mapa">
      <EnvironmentOutlined
        style={{ color: '#1677ff', cursor: 'pointer', marginLeft: 4 }}
        onClick={() => onFly([lat, lng])}
      />
    </Tooltip>
  );
};

// ── main component ────────────────────────────────────────────

const CollectionDetailModal: React.FC<CollectionDetailModalProps> = ({
  collectionId,
  open,
  onClose,
}) => {
  const history = useHistory();
  const { data: detail, isLoading } = useGetCollectionDetail(collectionId);
  const [focusedPoint, setFocusedPoint] = useState<[number, number] | null>(
    null,
  );
  const [showGeofenceEvents, setShowGeofenceEvents] = useState(true);
  const [showStatusLogs, setShowStatusLogs] = useState(false);
  const [showShipments, setShowShipments] = useState(false);
  const [shipmentStoreFilter, setShipmentStoreFilter] = useState<
    number | undefined
  >(undefined);
  const [loadShipments, setLoadShipments] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string[] | null>(null);
  // 'general' = full export; number = CollectionDetailStore.id for per-store
  const [exportTarget, setExportTarget] = useState<'general' | number | null>(
    null,
  );

  useEffect(() => {
    if (!open) {
      setFocusedPoint(null);
      setExportTarget(null);
    }
  }, [open]);

  // geofence_events.store_id matches stores[].store_id
  const storeMap = useMemo<Record<number, string>>(() => {
    if (!detail) return {};
    return Object.fromEntries(
      detail.stores.map((s) => [s.storeId, s.storeName]),
    );
  }, [detail]);

  // pickup_logs.store_id matches stores[].id (join table row id)
  const storeMapById = useMemo<Record<number, string>>(() => {
    if (!detail) return {};
    return Object.fromEntries(
      detail.stores.map((s) => [s.storeId, s.storeName]),
    );
  }, [detail]);

  const mapPoints = useMemo<MapPoint[]>(() => {
    if (!detail) return [];
    const pts: MapPoint[] = detail.stores
      .filter((s) => s.storeLat && s.storeLng)
      .map((s) => ({
        lat: s.storeLat,
        lng: s.storeLng,
        label: s.storeName,
        type: 'store',
      }));
    if (detail.depositLat && detail.depositLng) {
      pts.push({
        lat: detail.depositLat,
        lng: detail.depositLng,
        label: detail.depositName ?? 'Depósito',
        type: 'deposit',
      });
    }
    return pts;
  }, [detail]);

  const mapCenter = useMemo<[number, number]>(() => {
    if (!mapPoints.length) return [-34.6, -58.38];
    const lats = mapPoints.map((p) => p.lat);
    const lngs = mapPoints.map((p) => p.lng);
    return [
      (Math.min(...lats) + Math.max(...lats)) / 2,
      (Math.min(...lngs) + Math.max(...lngs)) / 2,
    ];
  }, [mapPoints]);

  const storeColumns: TableColumnsType<CollectionDetailStore> = [
    {
      title: 'Tienda',
      key: 'store_name',
      render: (_, s) => (
        <div>
          <Text strong style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
            {s.storeName}
          </Text>
          <div
            style={{
              fontSize: 12,
              color: '#8c8c8c',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              marginTop: 2,
            }}
          >
            {s.storeAddress || '-'}
          </div>
        </div>
      ),
    },
    {
      title: 'MP',
      dataIndex: 'marketplaceName',
      key: 'marketplaceName',
      width: 90,
      render: (v: string) => <Text style={{ fontSize: 12 }}>{v}</Text>,
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (v: string) => (
        <Tag
          color={STORE_STATUS_COLORS[v] ?? 'default'}
          style={{ fontSize: 11 }}
        >
          {STORE_STATUS_LABELS[v] ?? v}
        </Tag>
      ),
    },
    {
      title: 'Recogida',
      dataIndex: 'collectedAt',
      key: 'collectedAt',
      width: 90,
      render: (v: string | null) =>
        v ? (
          <Text style={{ fontSize: 12 }}>
            {new Date(v).toLocaleString('es-AR', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Text>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            —
          </Text>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 180,
      align: 'right' as const,
      render: (_, s) => (
        <div
          style={{
            display: 'flex',
            gap: 6,
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          {s.storeLat && s.storeLng && (
            <Tooltip title="Geolocalizar">
              <Button
                type="text"
                icon={
                  <EnvironmentOutlined
                    style={{ color: '#1677ff', fontSize: 15 }}
                  />
                }
                onClick={() => setFocusedPoint([s.storeLat, s.storeLng])}
              />
            </Tooltip>
          )}
          <Tooltip title="Exportar remito">
            <Button
              type="text"
              icon={<FilePdfOutlined style={{ fontSize: 15 }} />}
              loading={
                typeof exportTarget === 'number' &&
                exportTarget === s.id &&
                shipmentsLoading
              }
              onClick={() => handleExportByStore(s)}
            />
          </Tooltip>
          <Button
            size="small"
            icon={<LoginOutlined />}
            disabled={
              detail?.status === 'cancelled' ||
              s.status === 'received' ||
              s.status === 'cancelled'
            }
            onClick={() =>
              history.push(
                `${SITELINKS.collections.intake}?code=${collectionId}-${s.storeId}`,
              )
            }
          >
            Ingresar
          </Button>
        </div>
      ),
    },
  ];

  const geofenceColumns: TableColumnsType<CollectionGeofenceEvent> = [
    {
      title: 'Evento',
      dataIndex: 'eventType',
      key: 'eventType',
      width: 80,
      render: (v: string) => (
        <Tag color={EVENT_TYPE_COLORS[v] ?? 'default'} style={{ fontSize: 11 }}>
          {EVENT_TYPE_LABELS[v] ?? v}
        </Tag>
      ),
    },
    {
      title: 'Lugar',
      key: 'locationType',
      width: 120,
      render: (_, ev) => {
        if (ev.locationType === 'store') {
          return ev.storeId ? (storeMap[ev.storeId] ?? 'Tienda') : 'Tienda';
        }
        if (ev.locationType === 'deposit') return 'Depósito';
        return ev.locationType;
      },
    },
    {
      title: 'Hora',
      dataIndex: 'occurredAt',
      key: 'occurredAt',
      render: (v: string) =>
        new Date(v).toLocaleString('es-AR', {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        }),
    },
    {
      title: '',
      key: 'geo',
      width: 32,
      render: (_, ev) => (
        <GeoBtn lat={ev.lat} lng={ev.lng} onFly={setFocusedPoint} />
      ),
    },
  ];

  const statusLogColumns: TableColumnsType<CollectionStatusLog> = [
    {
      title: 'Tienda',
      dataIndex: 'collectionRequestStoreId',
      key: 'collectionRequestStoreId',
      render: (v: string) => (
        <Text style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
          {storeMap[Number(v)]}
        </Text>
      ),
    },
    {
      title: 'Desde',
      dataIndex: 'fromStatus',
      key: 'fromStatus',
      width: 130,
      render: (v: string) => (
        <Tag
          color={STORE_STATUS_COLORS[v] ?? 'default'}
          style={{ fontSize: 11 }}
        >
          {STORE_STATUS_LABELS[v] ?? v}
        </Tag>
      ),
    },
    {
      title: 'Hasta',
      dataIndex: 'toStatus',
      key: 'toStatus',
      width: 130,
      render: (v: string) => (
        <Tag
          color={STORE_STATUS_COLORS[v] ?? 'default'}
          style={{ fontSize: 11 }}
        >
          {STORE_STATUS_LABELS[v] ?? v}
        </Tag>
      ),
    },
    {
      title: 'Actor',
      dataIndex: 'actorName',
      key: 'actorName',
      render: (v: string) => <Text style={{ fontSize: 12 }}>{v}</Text>,
    },
    {
      title: 'Motivo',
      dataIndex: 'reason',
      key: 'reason',
      render: (v: string | null) => (
        <Text style={{ fontSize: 12 }}>{v ?? '—'}</Text>
      ),
    },
    {
      title: 'Fecha',
      dataIndex: 'occurredAt',
      key: 'occurredAt',
      width: 110,
      render: (v: string) =>
        new Date(v).toLocaleString('es-AR', {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        }),
    },
  ];

  const { data: shipmentsData, isLoading: shipmentsLoading } =
    useGetCollectionShipments(collectionId, loadShipments || showShipments);

  useEffect(() => {
    if (exportTarget === null || shipmentsLoading || !shipmentsData || !detail)
      return;
    if (exportTarget === 'general') {
      exportCollectionRemito(detail, shipmentsData);
    } else {
      exportCollectionRemito(detail, shipmentsData, { storeId: exportTarget });
    }
    setExportTarget(null);
  }, [exportTarget, shipmentsLoading, shipmentsData, detail]);

  const handleOpenShipments = () => {
    setShipmentStoreFilter(undefined);
    setShowShipments(true);
  };

  const handleExportGeneral = () => {
    setLoadShipments(true);
    setExportTarget('general');
  };

  const handleExportByStore = (store: CollectionDetailStore) => {
    setLoadShipments(true);
    setExportTarget(store.storeId);
  };

  const filteredShipments = useMemo(() => {
    const all = shipmentsData ?? [];
    return shipmentStoreFilter
      ? all.filter((s) => s.collection_request_store_id === shipmentStoreFilter)
      : all;
  }, [shipmentsData, shipmentStoreFilter]);

  const shipmentColumns: TableColumnsType<CollectionShipment> = [
    {
      title: 'Tienda',
      dataIndex: 'collection_request_store_id',
      key: 'collection_request_store_id',
      render: (v: string) => (
        <Text style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
          {detail?.stores.find((s) => s.storeId.toString() === v.toString())
            ?.storeName ?? v}
        </Text>
      ),
    },
    {
      title: 'Código externo',
      dataIndex: 'external_code',
      key: 'external_code',
    },
    {
      title: 'Fecha',
      dataIndex: 'created_at',
      align: 'right',
      key: 'created_at',
      render: (v: string) =>
        new Date(v).toLocaleString('es-AR', {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        }),
    },
  ];

  const pickupColumns: TableColumnsType<CollectionPickupLog> = [
    {
      title: 'Tienda',
      key: 'storeName',
      render: (_, pl) => (pl.storeId ? (storeMapById[pl.storeId] ?? '—') : '—'),
    },
    {
      title: 'Paquetes',
      dataIndex: 'packageCount',
      key: 'packageCount',
      width: 90,
    },
    {
      title: 'Hora',
      dataIndex: 'occurredAt',
      key: 'occurredAt',
      render: (v: string) =>
        new Date(v).toLocaleString('es-AR', {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        }),
    },
    {
      title: '',
      key: 'geo',
      width: 32,
      render: (_, pl) => (
        <GeoBtn lat={pl.lat} lng={pl.lng} onFly={setFocusedPoint} />
      ),
    },
    {
      title: '',
      key: 'photos',
      width: 32,
      render: (_, pl) =>
        pl.photos && pl.photos.length > 0 ? (
          <Tooltip title="Ver foto">
            <Button
              type="text"
              size="small"
              icon={<PictureOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                setPhotoPreview(pl.photos!);
              }}
            />
          </Tooltip>
        ) : null,
    },
  ];

  return (
    <>
      <Modal
        title={`Detalle de Colecta #${collectionId ?? ''}`}
        open={open}
        onCancel={onClose}
        footer={null}
        destroyOnClose
        width="90vw"
        styles={{
          body: { padding: 0, overflow: 'hidden' },
        }}
      >
        {isLoading && (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        )}

        {detail && !isLoading && (
          <Row gutter={0} style={{ height: 620 }}>
            {/* ── left: data ── */}
            <Col
              span={17}
              style={{
                height: '100%',
                overflowY: 'auto',
                padding: '20px 20px 24px 24px',
                borderRight: '1px solid #f0f0f0',
                display: 'flex',
                flexDirection: 'column',
                gap: 20,
              }}
            >
              {/* status + meta */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                <Tag
                  color={STATUS_COLORS[detail.status] ?? 'default'}
                  style={{ margin: 0, fontSize: 12 }}
                >
                  {STATUS_LABELS[detail.status] ?? detail.status}
                </Tag>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Creado{' '}
                  {new Date(detail.createdAt).toLocaleString('es-AR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Text>
              </div>

              {/* driver */}
              <div>
                <SectionTitle>Driver</SectionTitle>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '10px 16px',
                  }}
                >
                  <InfoItem label="Nombre">{detail.driverName || '—'}</InfoItem>
                  <InfoItem label="DNI">{detail.driverDni || '—'}</InfoItem>
                  <InfoItem label="Patente">
                    {detail.vehiclePlate || '—'}
                  </InfoItem>
                  <InfoItem label="Notificaciones">
                    {detail.notifyStore ? 'Tienda ' : ''}
                    {detail.notifyDriver ? 'Driver' : ''}
                    {!detail.notifyStore && !detail.notifyDriver
                      ? 'Ninguna'
                      : ''}
                  </InfoItem>
                </div>
              </div>

              {/* deposit */}
              {detail.depositName && (
                <div>
                  <SectionTitle>Depósito</SectionTitle>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 8,
                    }}
                  >
                    <div>
                      <Text strong style={{ fontSize: 13 }}>
                        {detail.depositName}
                      </Text>
                      {detail.depositAddress && (
                        <Text
                          type="secondary"
                          style={{
                            display: 'block',
                            fontSize: 12,
                            marginTop: 2,
                          }}
                        >
                          {detail.depositAddress}
                        </Text>
                      )}
                    </div>
                    <GeoBtn
                      lat={detail.depositLat}
                      lng={detail.depositLng}
                      onFly={setFocusedPoint}
                    />
                  </div>
                </div>
              )}

              {/* stores */}
              <div>
                <SectionTitle>Tiendas</SectionTitle>
                <Table
                  dataSource={detail.stores}
                  columns={storeColumns}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  style={{ fontSize: 12 }}
                />
              </div>

              {/* geofence events */}
              {detail.geofenceEvents.length > 0 && (
                <div>
                  <SectionTitle>Eventos de geofencing</SectionTitle>
                  <Table
                    dataSource={detail.geofenceEvents}
                    columns={geofenceColumns}
                    rowKey="id"
                    size="small"
                    pagination={false}
                  />
                </div>
              )}

              {/* pickup logs */}
              {detail.pickupLogs.length > 0 && (
                <div>
                  <SectionTitle>Logs de recolección</SectionTitle>
                  <Table
                    dataSource={detail.pickupLogs}
                    columns={pickupColumns}
                    rowKey="id"
                    size="small"
                    pagination={false}
                  />
                </div>
              )}
              {photoPreview && (
                <Image.PreviewGroup
                  preview={{
                    visible: true,
                    onVisibleChange: (vis) => {
                      if (!vis) setPhotoPreview(null);
                    },
                  }}
                >
                  {photoPreview.map((src, i) => (
                    <Image key={i} src={src} style={{ display: 'none' }} />
                  ))}
                </Image.PreviewGroup>
              )}
            </Col>

            {/* ── right: map + actions ── */}
            <Col
              span={7}
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                {mapPoints.length > 0 ? (
                  <>
                    {/* top-left: geofencing badge */}
                    <div
                      style={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        zIndex: 1000,
                        background: 'rgba(255,255,255,0.9)',
                        borderRadius: 4,
                        padding: '3px 8px',
                        fontSize: 11,
                        pointerEvents: 'none',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                      }}
                    >
                      Geofencing {GEOFENCE_RADIUS_METERS}m
                    </div>

                    {/* top-right: layer toggles */}
                    {detail.geofenceEvents.length > 0 && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 10,
                          right: 10,
                          zIndex: 1000,
                          background: 'rgba(255,255,255,0.95)',
                          borderRadius: 6,
                          padding: '6px 10px',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          fontSize: 12,
                        }}
                      >
                        <Switch
                          size="small"
                          checked={showGeofenceEvents}
                          onChange={setShowGeofenceEvents}
                        />
                        <span style={{ userSelect: 'none' }}>
                          Eventos geofencing
                        </span>
                      </div>
                    )}

                    <MapContainer
                      center={mapCenter}
                      zoom={14}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <MapInvalidator />
                      <FlyToController point={focusedPoint} />

                      {/* store + deposit markers with geofence circles */}
                      {mapPoints.map((pt) => (
                        <React.Fragment key={`${pt.type}-${pt.lat}-${pt.lng}`}>
                          <Marker
                            position={[pt.lat, pt.lng]}
                            icon={defaultIcon}
                          >
                            <Popup>
                              <strong>{pt.label}</strong>
                              <br />
                              {pt.type === 'deposit' ? 'Depósito' : 'Tienda'}
                            </Popup>
                          </Marker>
                          <Circle
                            center={[pt.lat, pt.lng]}
                            radius={GEOFENCE_RADIUS_METERS}
                            pathOptions={{
                              color:
                                pt.type === 'deposit' ? '#722ed1' : '#1677ff',
                              fillColor:
                                pt.type === 'deposit' ? '#722ed1' : '#1677ff',
                              fillOpacity: 0.15,
                              weight: 2,
                            }}
                          />
                        </React.Fragment>
                      ))}

                      {/* geofence event markers */}
                      {showGeofenceEvents &&
                        detail.geofenceEvents.map((ev) => (
                          <CircleMarker
                            key={ev.id}
                            center={[ev.lat, ev.lng]}
                            radius={7}
                            pathOptions={{
                              color:
                                ev.eventType === 'enter'
                                  ? '#52c41a'
                                  : '#ff7a45',
                              fillColor:
                                ev.eventType === 'enter'
                                  ? '#52c41a'
                                  : '#ff7a45',
                              fillOpacity: 0.85,
                              weight: 2,
                            }}
                          >
                            <Popup>
                              <strong>
                                {EVENT_TYPE_LABELS[ev.eventType] ??
                                  ev.eventType}
                              </strong>
                              <br />
                              {ev.locationType === 'store'
                                ? ev.storeId
                                  ? (storeMap[ev.storeId] ?? 'Tienda')
                                  : 'Tienda'
                                : ev.locationType === 'deposit'
                                  ? 'Depósito'
                                  : ev.locationType}
                              <br />
                              <span style={{ fontSize: 11, color: '#888' }}>
                                {new Date(ev.occurredAt).toLocaleString(
                                  'es-AR',
                                  {
                                    day: '2-digit',
                                    month: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  },
                                )}
                              </span>
                            </Popup>
                          </CircleMarker>
                        ))}
                    </MapContainer>
                  </>
                ) : (
                  <div
                    style={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#fafafa',
                    }}
                  >
                    <Text type="secondary">Sin coordenadas disponibles</Text>
                  </div>
                )}
              </div>

              {/* bottom-right action bar */}
              <div
                style={{ padding: '10px 12px', borderTop: '1px solid #f0f0f0' }}
              >
                <Space
                  size="small"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  {detail.statusLogs?.length > 0 && (
                    <Button
                      size="small"
                      icon={<HistoryOutlined />}
                      onClick={() => setShowStatusLogs(true)}
                      style={{ fontSize: 11 }}
                    >
                      Historial ({detail.statusLogs.length})
                    </Button>
                  )}
                  <Button
                    size="small"
                    onClick={handleOpenShipments}
                    style={{ fontSize: 11 }}
                  >
                    Envíos
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    icon={<FilePdfOutlined />}
                    loading={exportTarget === 'general' && shipmentsLoading}
                    onClick={handleExportGeneral}
                    style={{ fontSize: 11 }}
                  >
                    Exportar remito
                  </Button>
                </Space>
              </div>
            </Col>
          </Row>
        )}
      </Modal>

      {detail && (
        <Modal
          title={`Historial de estados — Colecta #${collectionId ?? ''}`}
          open={showStatusLogs}
          onCancel={() => setShowStatusLogs(false)}
          footer={null}
          width={800}
          destroyOnClose
        >
          <Table
            dataSource={detail.statusLogs}
            columns={statusLogColumns}
            rowKey="id"
            size="small"
            pagination={false}
          />
        </Modal>
      )}

      {detail && (
        <Modal
          title={`Envíos — Colecta #${collectionId ?? ''}`}
          open={showShipments}
          onCancel={() => setShowShipments(false)}
          footer={null}
          width={700}
          destroyOnClose
        >
          <div style={{ marginBottom: 12 }}>
            <Select
              allowClear
              placeholder="Filtrar por tienda"
              style={{ width: 240 }}
              value={shipmentStoreFilter}
              onChange={setShipmentStoreFilter}
              options={detail.stores.map((s) => ({
                value: String(s.id),
                label: s.storeName,
              }))}
            />
          </div>
          <Table
            dataSource={filteredShipments}
            columns={shipmentColumns}
            rowKey="id"
            size="small"
            pagination={false}
            loading={shipmentsLoading}
          />
        </Modal>
      )}
    </>
  );
};

export default CollectionDetailModal;
